import moment from 'moment'
import Cookies from 'js-cookie'
import {identity, isEmpty, isEqual, keyBy, omit, pickBy, union} from 'lodash'
import queryString from 'query-string'
import {actionCreators} from 'actions'
import api from 'api'
import {formValueSelector, getFormValues, autofill as autofillForm} from 'redux-form'
import {groupFullName} from 'employees/form/EmploymentInfomation'

export const formName = 'employeeSearchForm'
export const selector = formValueSelector(formName)
export const getValues = getFormValues(formName)
export const autofill = autofillForm.bind(null, formName)
export const defaultQuery = {
  employment_status: 'employed',
  display_type: 'default',
  _page: '1',
  sort: 'staff_code',
  order: 'asc',
}
const defaultLimit = '100'

export const convertQueryToForm = (query) => {
  return Object.assign(
    {sort_type: `${query.sort}__${query.order}`},
    ...Object.entries(query)
      .map(([k, v]) => convertQueryToFormField(k, v))
      .filter(identity)
  )
}

export const convertQueryToFormField = (fieldName, value) => {
  switch (fieldName) {
    case 'office_ids':
      return {offices: value.join(',')}
    case 'group_ids':
      return {groups: value.join(',')}
    case 'employment_status_ids':
      return {employment_statuses: value}
    case 'position_ids':
      return {positions: value}
    case 'employment_type_ids':
      return {employment_types: value}
    case 'occupation_ids':
      return {occupations: value}
    case '_page':
      return {page: value}
    case 'sort':
    case 'order':
      return undefined
    case 'tag_ids':
      return {tags: value.join(',')}
    case 'skills':
      return {skills: value.join(',')}
    case 'qualifications':
      return {qualifications: value.join(',')}
    case 'language_ids':
      return {languages: value}
    case 'language_level_ids':
      return {language_levels: value}
    case 'languages_qualification_name':
      return {languages_qualification_name: value}
    case 'projects':
      return {projects: value.join(',')}
    case 'comment_colors':
      return {comment_colors: value}
    default:
      return {[fieldName]: value}
  }
}

export const convertFormToQueryString = (data) =>
  queryString.stringify(convertFormToQuery(data), {arrayFormat: 'bracket'})

export const convertFormToQuery = (data) => {
  if (data === undefined) return {}
  return Object.assign(
    {},
    ...Object.entries(data)
      .map(([k, v]) => convertFormToQueryField(k, v))
      .filter(identity)
  )
}

export const convertFormToQueryField = (fieldName, value) => {
  switch (fieldName) {
    case 'offices': {
      const officesIds = value.split(',')
      return !isEqual(officesIds, ['']) ? {office_ids: officesIds} : null
    }
    case 'groups': {
      const groupIds = value.split(',')
      return !isEqual(groupIds, ['']) ? {group_ids: groupIds} : null
    }
    case 'employment_statuses':
      return !isEmpty(value) ? {employment_status_ids: value} : null
    case 'positions':
      return !isEmpty(value) ? {position_ids: value} : null
    case 'employment_types':
      return !isEmpty(value) ? {employment_type_ids: value} : null
    case 'occupations':
      return !isEmpty(value) ? {occupation_ids: value} : null
    case 'joined_health_insurance':
    case 'joined_welfare_pension_insurance':
    case 'joined_employment_insurance':
      return value !== '' ? {[fieldName]: value} : null
    case 'limit':
      return null
    case 'page':
      return {_page: value}
    case 'sort_type': {
      const [sort, order] = value.split('__')
      return {order, sort}
    }
    case 'tags': {
      return !isEmpty(value) ? {tag_ids: value.split(/,|\s/)} : null
    }
    case 'skills': {
      return !isEmpty(value) ? {skills: value.split(/,|\s/)} : null
    }
    case 'qualifications': {
      return !isEmpty(value) ? {qualifications: value.split(/,|\s/)} : null
    }
    case 'languages': {
      return !isEqual(value) ? {language_ids: value} : null
    }
    case 'language_levels': {
      return !isEqual(value) ? {language_level_ids: value} : null
    }
    case 'languages_qualification_name': {
      return !isEqual(value) ? {languages_qualification_name: value} : null
    }
    case 'projects':
      return !isEmpty(value) ? {projects: value.split(/,|\s/)} : null
    case 'comment_colors':
      return !isEmpty(value) ? {comment_colors: value} : null
    default:
      return {[fieldName]: value}
  }
}

const addedFieldsKey = (user, pathname) => {
  const isEmployeeRelatedPage = ['employees', 'direct_request'].includes(pathname.split('/')[1])
  const prefix = isEmployeeRelatedPage ? '' : 'search_form'
  return `${prefix}#${user.id}__added_fields`
}

export const addOptions = [
  {label: '旧姓', value: 'former_name'},
  {label: '生年月日', value: 'birthday', fields: ['birthday_start', 'birthday_end']},
  {label: '性別 ', value: 'genders'},
  {label: '居住国', value: 'countries'},
  {label: '住所 都道府県', value: 'prefecture_ids'},
  {
    label: '国籍情報',
    value: 'national_types',
    fields: ['national_types', 'nation_ids', 'visa_type_ids'],
  },
  {label: '退職日', value: 'retired_at', fields: ['retired_at_start', 'retired_at_end']},
  {label: '退職区分', value: 'retire_types'},
  {label: '家族の有無', value: 'has_dependents'},
  {label: '配偶者の有無', value: 'marital_statuses'},
  {label: '給与形態', value: 'remuneration_types'},
  {label: '障害者区分', value: 'handicap_types'},
  {label: '寡婦（夫）区分 ', value: 'widow_types'},
  {label: '勤労学生区分', value: 'working_student_types'},
  {label: 'タグ', value: 'tags'},
  {label: 'スキル', value: 'skills'},
  {label: '資格', value: 'qualifications'},
  {
    label: '語学',
    value: 'languages',
    fields: [
      'languages',
      'language_levels',
      'languages_qualification_name',
      'languages_score_from',
      'languages_score_to',
    ],
  },
  {label: 'プロジェクト', value: 'projects'},
]

const makeAddedFields = (query, user, pathname) => {
  let savedQuery = []
  if (window.localStorage) {
    const saved = window.localStorage.getItem(addedFieldsKey(user, pathname))
    if (saved) {
      savedQuery = JSON.parse(saved)
    }
  }
  let hasQuery = addOptions
    .filter((opt) => (opt.fields || [opt.value]).some((key) => !isEmpty(query[key])))
    .map((opt) => opt.value)
  return union(savedQuery, hasQuery)
}

export const saveAddedFields = (user, addedField, location) => {
  if (window.localStorage) {
    window.localStorage.setItem(
      addedFieldsKey(user, location.pathname),
      JSON.stringify(isEmpty(addedField) ? [] : addedField)
    )
  }
}

export const getCurrentQueryFromLocation = (location, user) => {
  const joinedEmploymentInsurance = location.pathname === '/change_office' ? {joined_employment_insurance: true} : {}
  const query = {
    ...defaultQuery,
    age_base_date: moment().format('YYYY/MM/DD'),
    ...joinedEmploymentInsurance,
    ...queryString.parse(location.search, {arrayFormat: 'bracket'}),
    limit: getSavedDisplayEmployeeLimit(location.pathname, user),
  }
  return {...query, _added_fields: makeAddedFields(query, user, location.pathname)}
}

const limitKey = (pathname, user) => `${pathname.replace(/\//g, '')}#${user.id}__disp_emp_limit`

export const getSavedDisplayEmployeeLimit = (pathname, user) => {
  if (window.localStorage) {
    const limit = window.localStorage.getItem(limitKey(pathname, user))
    if (!isEmpty(limit)) {
      return limit
    }
  }
  const limit = Cookies.get(limitKey(pathname, user))
  return isEmpty(limit) ? defaultLimit : limit
}

export const saveDisplayEmployeeLimit = (pathname, user, limit) => {
  if (window.localStorage) {
    window.localStorage.setItem(limitKey(pathname, user), limit)
  } else {
    Cookies.set(limitKey(pathname, user), limit)
  }
}

export const fetchEmployees =
  (query, additionalParams = {}) =>
  (dispatch, getState) => {
    const {
      auth: {token},
    } = getState()
    const request = api.createWithAuth(token).employees.search({...query, ...additionalParams})
    dispatch(actionCreators.employees.list.fetchData(request))
  }

// MEMO: Layout呼び出し時に最新のofficeが取得されているのでここで再度取得する必要はない
export const fetchSelections = (token, dispatch) => {
  const promises = [
    api.createWithAuth(token).groups.list(),
    api.createWithAuth(token).employees.selections.positions(),
    api.createWithAuth(token).employees.selections.employmentTypes(),
    api.createWithAuth(token).employees.selections.occupations(),
    api.createWithAuth(token).employees.selections.tags(),
    api.createWithAuth(token).employees.selections.languages(),
    api.createWithAuth(token).employees.selections.languageLevels(),
  ]
  dispatch(
    actionCreators.fetchData(
      'selections',
      Promise.all(promises).then((results) => {
        const [
          groupsResult,
          positionsResult,
          employmentTypesResult,
          occupationsResult,
          tagsResult,
          languagesResult,
          languageLevelsResult,
        ] = results
        return {
          groups: groupsResult.groups.map((group) => ({
            value: `${group.id}`,
            label: groupFullName(group, keyBy(groupsResult.groups, 'id')),
            parent_value: `${group.parent_group_id}`,
          })),
          positions: positionsResult.data.map((item) => ({...item, id: `${item.id}`})),
          employmentTypes: employmentTypesResult.data.map((item) => ({...item, id: `${item.id}`})),
          occupations: occupationsResult.data.map((item) => ({...item, id: `${item.id}`})),
          tags: tagsResult.data.map((tag) => ({
            value: `${tag.id}`,
            label: tag.name,
          })),
          languages: languagesResult.data.map((item) => ({...item, id: `${item.id}`})),
          languageLevels: languageLevelsResult.data.map((item) => ({...item, id: `${item.id}`})),
        }
      })
    )
  )
}

const ignoreFields = (pathname) => {
  const fields = ['_added_fields', 'age_base_date']

  if (pathname === '/change_office') {
    fields.push('joined_employment_insurance')
  }

  return fields
}

export const hasConditions = (formValues, pathname = '') => {
  return !isEqual(pickBy(omit(convertFormToQuery(formValues), ignoreFields(pathname))), defaultQuery)
}
